"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Calendar, Clock } from "lucide-react";
export default function ScheduleYourAppointment() {
  const [isOpen, setIsOpen] = useState(false);
  return <section className="relative bg-background py-20 px-4">
      <div className="container mx-auto max-w-3xl text-center">
        <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">Contact Us</h2>
        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
          Get your motorcycle back to peak condition with our expert panel respray and crash repair services. Book your appointment today and experience precision restoration.
        </p>
        <Button onClick={() => setIsOpen(true)} className="btn-primary text-lg px-8 py-4 transition-transform duration-[80ms] hover:scale-[1.02]">Contact Details</Button>
      </div>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[90vh] overflow-y-auto transition-transform duration-[250ms] ease-in-out">
          <SheetHeader>
            <SheetTitle className="text-2xl font-display">Book Your Appointment</SheetTitle>
            <SheetDescription>
              Fill out the form below and we'll get back to you within 24 hours to confirm your appointment.
            </SheetDescription>
          </SheetHeader>

          <FormProvider formId="appointment-form-schedule" sectionName="schedule-your-appointment" defaultValues={{
          name: "",
          email: "",
          phone: "",
          motorcycle: "",
          service: "",
          preferredDate: "",
          preferredTime: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            minLength: 10
          },
          motorcycle: {
            required: true,
            minLength: 2
          },
          service: {
            required: true
          },
          preferredDate: {
            required: true
          },
          preferredTime: {
            required: true
          },
          message: {
            minLength: 0,
            maxLength: 500
          }
        }} submitText="Schedule Appointment" successMessage="Appointment request received! We'll contact you within 24 hours to confirm." className="mt-6">
            <AppointmentForm onSuccess={() => setIsOpen(false)} />
          </FormProvider>
        </SheetContent>
      </Sheet>
    </section>;
}
function AppointmentForm({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary-light border border-primary rounded-lg">
        <p className="text-foreground-primary text-center mb-4">{successMessage}</p>
        <Button onClick={() => {
        resetForm();
        onSuccess();
      }} variant="outline" className="w-full">
          Close
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-6">
        <div className="grid md:grid-cols-2 gap-4">
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} className="border-border" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          minLength: {
            value: 10,
            message: "Phone must be at least 10 digits"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input placeholder="(555) 123-4567" type="tel" {...field} className="border-border" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input placeholder="john@example.com" type="email" {...field} className="border-border" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="motorcycle" rules={{
        required: "Motorcycle make/model is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Motorcycle Make & Model</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Yamaha R1 2020" {...field} className="border-border" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Service Needed</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <option value="">Select a service</option>
                  <option value="panel-repaint">Motorcycle Panel Repaint</option>
                  <option value="crash-repair">Crash Repair & Structural Reinforcement</option>
                  <option value="frame-restoration">Frame Restoration</option>
                  <option value="dent-removal">Dent Removal</option>
                  <option value="paint-touchup">Paint Touch-Ups</option>
                  <option value="custom-graphics">Custom Graphics & Decals</option>
                  <option value="full-consultation">Full Project Consultation</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid md:grid-cols-2 gap-4">
          <FormField control={control} name="preferredDate" rules={{
          required: "Preferred date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Preferred Date</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type="date" {...field} className="border-border pl-10" />
                    <SafeHydrate><Calendar className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" /></SafeHydrate>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="preferredTime" rules={{
          required: "Preferred time is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Preferred Time</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type="time" {...field} className="border-border pl-10" />
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" rules={{
        maxLength: {
          value: 500,
          message: "Message must be less than 500 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Notes (Optional)</FormLabel>
              <FormControl>
                <textarea {...field} placeholder="Tell us more about your repair needs..." className="flex min-h-[100px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
          {isSubmitting ? <>
              <span className="animate-spin mr-2">⏳</span>
              Scheduling...
            </> : "Schedule Appointment"}
        </Button>
      </div>
    </Form>;
}